import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stage, Layer, Rect, Circle, Line, Text, Group, Transformer, Arrow } from 'react-konva';
import axios from 'axios';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { Layout } from '../components/Layout';
import CanvasToolbar from '../components/canvas/CanvasToolbar';
import CanvasContextMenu from '../components/canvas/CanvasContextMenu';
import CanvasCursors from '../components/canvas/CanvasCursors';
import CanvasPresence from '../components/canvas/CanvasPresence';
import TextPropertiesPanel from '../components/canvas/TextPropertiesPanel';
import StickyNotePropertiesPanel from '../components/canvas/StickyNotePropertiesPanel';
import ArrowPropertiesPanel from '../components/canvas/ArrowPropertiesPanel';
import ShapePropertiesPanel from '../components/canvas/ShapePropertiesPanel';
import DrawingPropertiesPanel from '../components/canvas/DrawingPropertiesPanel';
import StickyNote from '../components/canvas/elements/StickyNote';
import TextElement from '../components/canvas/elements/TextElement';
import ShapeElement from '../components/canvas/elements/ShapeElement';
import DrawingElement from '../components/canvas/elements/DrawingElement';
import ImageElement from '../components/canvas/elements/ImageElement';
import { useCanvasWebSocket } from '../hooks/useCanvasWebSocket';
import { useCanvasHistory } from '../hooks/useCanvasHistory';
import { ArrowLeft, Loader2, Plus, Workflow, GitBranch, Zap, Box, Diamond, Circle as CircleIcon, Play, Flag, ChevronDown, Minus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';


const API = process.env.REACT_APP_BACKEND_URL;

// Tool configurations
const TOOLS = {
  SELECT: 'select',
  PAN: 'pan',
  STICKY_NOTE: 'sticky_note',
  TEXT: 'text',
  SHAPE_RECT: 'shape_rect',
  SHAPE_CIRCLE: 'shape_circle',
  SHAPE_ARROW: 'shape_arrow',
  CONNECTOR: 'connector',
  CONNECTOR_VERTICAL: 'connector_vertical',
  CONNECTOR_DRAW: 'connector_draw',
  DRAW: 'draw',
  ERASER: 'eraser',
  IMAGE: 'image'
};

// Keyboard shortcuts
const SHORTCUTS = {
  'v': TOOLS.SELECT,
  'h': TOOLS.PAN,
  'n': TOOLS.STICKY_NOTE,
  't': TOOLS.TEXT,
  'r': TOOLS.SHAPE_RECT,
  'c': TOOLS.SHAPE_CIRCLE,
  'a': TOOLS.SHAPE_ARROW,
  'l': TOOLS.CONNECTOR,
  'k': TOOLS.CONNECTOR_VERTICAL,
  'j': TOOLS.CONNECTOR_DRAW,
  'd': TOOLS.DRAW,
  'e': TOOLS.ERASER
};

// Board background colors mapping
const BOARD_COLORS = {
  'white': '#FFFFFF',
  'light-gray': '#F3F4F6',
  'warm': '#FFFBEB',
  'cool': '#EFF6FF',
  'mint': '#ECFDF5',
  'lavender': '#F5F3FF',
  'dark': '#1F2937',
  'slate': '#0F172A'
};

// Helper function to simplify a path while preserving key points
const simplifyPath = (points, minDistance = 5) => {
  if (points.length <= 4) return points; // Already simple (2 points)
  
  const result = [];
  let lastX = points[0];
  let lastY = points[1];
  result.push(lastX, lastY);
  
  // Always include points that are far enough from the last added point
  for (let i = 2; i < points.length - 2; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    const dist = Math.sqrt((x - lastX) ** 2 + (y - lastY) ** 2);
    
    if (dist >= minDistance) {
      result.push(x, y);
      lastX = x;
      lastY = y;
    }
  }
  
  // Always include the last point
  result.push(points[points.length - 2], points[points.length - 1]);
  
  return result;
};

export default function CanvasEditor() {
  const { canvasId } = useParams();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);
  const nodeCounterRef = useRef(1);
  
  // Canvas state
  const [canvas, setCanvas] = useState(null);
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  
  // Tool state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [toolOptions, setToolOptions] = useState({
    strokeColor: '#000000',
    fillColor: '#FFEB3B',
    strokeWidth: 2,
    fontSize: 16,
    textColor: '#FFFFFF'
  });
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionRect, setSelectionRect] = useState(null);
  
  // Viewport state
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  
  // Board settings from canvas
  const [boardBackgroundColor, setBoardBackgroundColor] = useState('#FFFFFF');
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  
  // Context menu
  const [contextMenu, setContextMenu] = useState(null);
  
  // Automation Quick Create state
  const [automationPrompt, setAutomationPrompt] = useState('');
  const [showAutomationPopover, setShowAutomationPopover] = useState(false);
  const [flowStartType, setFlowStartType] = useState('trigger');
  const [flowTileColor, setFlowTileColor] = useState('#3B82F6');
  const [flowArrowColor, setFlowArrowColor] = useState('#000000');
  
  // Tree builder state - hierarchical node structure for asymmetric trees
  // Each node: { id, label, children: [...nodes] }
  const [treeRoot, setTreeRoot] = useState({
    id: 'root',
    label: 'Start',
    children: []
  });
  
  // History (undo/redo)
  const { 
    pushHistory, 
    undo, 
    redo, 
    canUndo, 
    canRedo 
  } = useCanvasHistory(elements, setElements);
  
  // WebSocket for real-time collaboration
  const {
    isConnected,
    activeUsers,
    cursors,
    activeEdits,
    sendCursorMove,
    sendElementCreate,
    sendElementUpdate,
    sendElementDelete,
    sendBatchUpdate,
    sendBatchDelete,
    sendStartEditing,
    sendStopEditing
  } = useCanvasWebSocket(canvasId, {
    onElementCreated: (data) => {
      setElements(prev => [...prev, data.element]);
    },
    onElementUpdated: (data) => {
      setElements(prev => prev.map(el => 
        el.id === data.element_id ? { ...el, ...data.updates } : el
      ));
    },
    onElementDeleted: (data) => {
      setElements(prev => prev.filter(el => el.id !== data.element_id));
    },
    onBatchUpdated: (data) => {
      setElements(prev => {
        const updateMap = new Map(data.updates.map(u => [u.id, u]));
        return prev.map(el => updateMap.has(el.id) ? { ...el, ...updateMap.get(el.id) } : el);
      });
    },
    onBatchDeleted: (data) => {
      const deleteSet = new Set(data.element_ids);
      setElements(prev => prev.filter(el => !deleteSet.has(el.id)));
    }
  });

  // Fetch canvas data
  useEffect(() => {
    const fetchCanvas = async () => {
      try {
        const response = await axios.get(`${API}/api/canvas/${canvasId}`);
        setCanvas(response.data);
        setElements(response.data.elements || []);
        setCanEdit(response.data.user_role === 'owner' || response.data.user_role === 'editor');
        
        // Apply canvas settings
        if (response.data.settings) {
          const settings = response.data.settings;
          
          // Apply board background color
          if (settings.boardColor && BOARD_COLORS[settings.boardColor]) {
            setBoardBackgroundColor(BOARD_COLORS[settings.boardColor]);
          }
          
          // Apply default flowchart colors
          if (settings.defaultTileColor) {
            setFlowTileColor(settings.defaultTileColor);
          }
          if (settings.defaultArrowColor) {
            setFlowArrowColor(settings.defaultArrowColor);
          }
        }
        
        // Restore viewport if saved
        if (response.data.viewport) {
          setStagePos({ x: response.data.viewport.x || 0, y: response.data.viewport.y || 0 });
          setStageScale(response.data.viewport.zoom || 1);
        }
      } catch (error) {
        console.error('Error fetching canvas:', error);
        toast.error('Failed to load canvas');
        navigate('/canvas');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCanvas();
  }, [canvasId, navigate]);

  // Handle window resize
  useEffect(() => {
    let resizeTimeout;
    const updateSize = () => {
      // Debounce resize to prevent ResizeObserver loop errors
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          const height = containerRef.current.offsetHeight;
          if (width > 0 && height > 0) {
            setStageSize({ width, height });
          }
        }
      }, 50);
    };
    
    // Initial size update with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateSize, 100);
    
    updateSize();
    window.addEventListener('resize', updateSize);
    
    // Use ResizeObserver for container changes
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', updateSize);
      resizeObserver?.disconnect();
    };
  }, [loading]); // Re-run when loading changes

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const key = e.key.toLowerCase();
      
      // Tool shortcuts
      if (SHORTCUTS[key] && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setActiveTool(SHORTCUTS[key]);
        return;
      }
      
      // Delete
      if ((key === 'delete' || key === 'backspace') && selectedIds.length > 0) {
        e.preventDefault();
        handleDeleteSelected();
        return;
      }
      
      // Undo
      if ((e.metaKey || e.ctrlKey) && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      
      // Redo
      if ((e.metaKey || e.ctrlKey) && ((key === 'z' && e.shiftKey) || key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
      
      // Escape - deselect
      if (key === 'escape') {
        setSelectedIds([]);
        setContextMenu(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, undo, redo]);

  // Get current user info
  const currentUser = useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  // Create element helper
  const createElement = useCallback(async (type, x, y, additionalProps = {}) => {
    if (!canEdit) return null;
    
    const now = new Date().toISOString();
    const passedProps = additionalProps.properties || {};
    const element = {
      id: uuidv4(),
      type,
      x,
      y,
      rotation: 0,
      scale_x: 1,
      scale_y: 1,
      z_index: elements.length,
      properties: {},
      created_by: currentUser?.user_id || currentUser?.id,
      created_at: now,
      updated_at: now,
      width: additionalProps.width,
      height: additionalProps.height
    };
    
    // Set default properties based on type, merging with passed properties
    switch (type) {
      case 'sticky_note':
        element.width = additionalProps.width || 200;
        element.height = additionalProps.height || 200;
        element.properties = {
          text: '',
          color: toolOptions.fillColor || '#FFEB3B',
          ...passedProps
        };
        break;
      case 'text':
        element.properties = {
          text: 'Double-click to edit',
          fontSize: toolOptions.fontSize || 16,
          fontFamily: 'Inter',
          fill: toolOptions.textColor || '#FFFFFF',
          align: 'left',
          ...passedProps
        };
        break;
      case 'shape_rect':
        element.width = additionalProps.width || 150;
        element.height = additionalProps.height || 100;
        element.properties = {
          fill: toolOptions.fillColor || 'transparent',
          stroke: toolOptions.strokeColor || '#000000',
          strokeWidth: toolOptions.strokeWidth || 2,
          ...passedProps
        };
        break;
      case 'shape_circle':
        element.width = additionalProps.width || 100;
        element.height = additionalProps.height || 100;
        element.properties = {
          fill: toolOptions.fillColor || 'transparent',
          stroke: toolOptions.strokeColor || '#000000',
          strokeWidth: toolOptions.strokeWidth || 2,
          ...passedProps
        };
        break;
      case 'shape_arrow':
        element.properties = {
          points: [0, 0, 150, 0],
          stroke: toolOptions.strokeColor || '#000000',
          strokeWidth: toolOptions.strokeWidth || 2,
          pointerLength: 10,
          pointerWidth: 10,
          ...passedProps
        };
        break;
      case 'connector':
        element.properties = {
          points: [0, 0, 200, 0],
          stroke: toolOptions.strokeColor || '#000000',
          strokeWidth: toolOptions.strokeWidth || 2,
          pointerLength: 10,
          pointerWidth: 10,
          pointerAtBeginning: true,
          pointerAtEnd: true,
          ...passedProps
        };
        break;
      case 'connector_vertical':
        element.properties = {
          points: [0, 0, 0, 150],
          stroke: toolOptions.strokeColor || '#000000',
          strokeWidth: toolOptions.strokeWidth || 2,
          pointerLength: 10,
          pointerWidth: 10,
          pointerAtBeginning: true,
          pointerAtEnd: true,
          ...passedProps
        };
        break;
      case 'connector_drawn':
        // Drawn connector with custom points - preserve the path shape
        const connectorProps = passedProps;
        // Simplify points slightly to reduce noise but keep the overall shape
        const rawPoints = connectorProps.points || [0, 0, 100, 100];
        const simplifiedPoints = simplifyPath(rawPoints, 5); // Keep every 5th point minimum
        element.properties = {
          points: simplifiedPoints,
          stroke: connectorProps.stroke || toolOptions.strokeColor || '#000000',
          strokeWidth: connectorProps.strokeWidth || toolOptions.strokeWidth || 2,
          pointerLength: 10,
          pointerWidth: 10,
          pointerAtBeginning: true,
          pointerAtEnd: true,
          tension: 0 // No smoothing - preserve the exact path
        };
        break;
      case 'drawing':
        // additionalProps may contain properties object with drawing data
        const drawingProps = passedProps;
        element.properties = {
          points: drawingProps.points || [],
          stroke: drawingProps.stroke || toolOptions.strokeColor || '#000000',
          strokeWidth: drawingProps.strokeWidth || toolOptions.strokeWidth || 2,
          tension: 0.5,
          lineCap: 'round',
          lineJoin: 'round'
        };
        break;
      case 'image':
        element.properties = {
          src: passedProps.src || '',
          originalWidth: passedProps.originalWidth,
          originalHeight: passedProps.originalHeight
        };
        break;
      default:
        element.properties = { ...passedProps };
        break;
    }
    
    // Add to local state
    pushHistory();
    setElements(prev => [...prev, element]);
    
    // Save to server
    try {
      await axios.post(`${API}/api/canvas/${canvasId}/elements`, {
        type: element.type,
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
        rotation: element.rotation,
        z_index: element.z_index,
        properties: element.properties
      });
      
      // Broadcast to others
      sendElementCreate(element);
    } catch (error) {
      console.error('Error creating element:', error);
    }
    
    return element;
  }, [canEdit, elements.length, currentUser, toolOptions, canvasId, pushHistory, sendElementCreate]);

  // Update element helper
  const updateElement = useCallback(async (elementId, updates, broadcast = true) => {
    if (!canEdit) return;
    
    setElements(prev => prev.map(el => 
      el.id === elementId ? { ...el, ...updates, updated_at: new Date().toISOString() } : el
    ));
    
    // Debounced save to server
    try {
      await axios.patch(`${API}/api/canvas/${canvasId}/elements/${elementId}`, updates);
      if (broadcast) {
        sendElementUpdate(elementId, updates);
      }
    } catch (error) {
      console.error('Error updating element:', error);
    }
  }, [canEdit, canvasId, sendElementUpdate]);

  // Delete selected elements
  const handleDeleteSelected = useCallback(async () => {
    if (!canEdit || selectedIds.length === 0) return;
    
    pushHistory();
    
    const idsToDelete = [...selectedIds];
    setElements(prev => prev.filter(el => !idsToDelete.includes(el.id)));
    setSelectedIds([]);
    
    try {
      await axios.post(`${API}/api/canvas/${canvasId}/elements/delete-batch`, idsToDelete);
      sendBatchDelete(idsToDelete);
    } catch (error) {
      console.error('Error deleting elements:', error);
    }
  }, [canEdit, selectedIds, canvasId, pushHistory, sendBatchDelete]);

  // Stage event handlers
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    
    const stage = stageRef.current;
    const oldScale = stageScale;
    const pointer = stage.getPointerPosition();
    
    const mousePointTo = {
      x: (pointer.x - stagePos.x) / oldScale,
      y: (pointer.y - stagePos.y) / oldScale
    };
    
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? oldScale * 1.1 : oldScale / 1.1;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));
    
    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale
    };
    
    setStageScale(clampedScale);
    setStagePos(newPos);
  }, [stageScale, stagePos]);

  const handleMouseDown = useCallback((e) => {
    // Right click - show context menu
    if (e.evt.button === 2) {
      e.evt.preventDefault();
      const stage = stageRef.current;
      const pointer = stage.getPointerPosition();
      
      setContextMenu({
        x: pointer.x,
        y: pointer.y,
        stageX: (pointer.x - stagePos.x) / stageScale,
        stageY: (pointer.y - stagePos.y) / stageScale
      });
      return;
    }
    
    // Close context menu on any click
    setContextMenu(null);
    
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const stageX = (pointer.x - stagePos.x) / stageScale;
    const stageY = (pointer.y - stagePos.y) / stageScale;
    
    // Check if clicking on empty area
    const clickedOnEmpty = e.target === e.target.getStage();
    
    if (activeTool === TOOLS.PAN) {
      // Pan mode - just let the drag handler take over
      return;
    }
    
    if (activeTool === TOOLS.SELECT) {
      if (clickedOnEmpty) {
        // Start selection rectangle
        setSelectionRect({ x: stageX, y: stageY, width: 0, height: 0 });
        setSelectedIds([]);
      }
      return;
    }
    
    if (!canEdit) return;
    
    // Create element based on tool
    if (activeTool === TOOLS.STICKY_NOTE) {
      createElement('sticky_note', stageX - 100, stageY - 100);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.TEXT) {
      createElement('text', stageX, stageY);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.SHAPE_RECT) {
      createElement('shape_rect', stageX - 75, stageY - 50);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.SHAPE_CIRCLE) {
      createElement('shape_circle', stageX - 50, stageY - 50);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.SHAPE_ARROW) {
      createElement('shape_arrow', stageX, stageY);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.CONNECTOR) {
      createElement('connector', stageX, stageY);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.CONNECTOR_VERTICAL) {
      createElement('connector_vertical', stageX, stageY);
      setActiveTool(TOOLS.SELECT);
    } else if (activeTool === TOOLS.CONNECTOR_DRAW) {
      // Start drawing a connector
      setIsDrawing(true);
      setCurrentDrawing({
        type: 'connector_drawn',
        points: [stageX, stageY],
        stroke: toolOptions.strokeColor,
        strokeWidth: toolOptions.strokeWidth
      });
    } else if (activeTool === TOOLS.DRAW || activeTool === TOOLS.ERASER) {
      setIsDrawing(true);
      setCurrentDrawing({
        type: 'drawing',
        points: [stageX, stageY],
        stroke: activeTool === TOOLS.ERASER ? '#FFFFFF' : toolOptions.strokeColor,
        strokeWidth: activeTool === TOOLS.ERASER ? 20 : toolOptions.strokeWidth
      });
    }
  }, [activeTool, stagePos, stageScale, canEdit, createElement, toolOptions]);

  const handleMouseMove = useCallback((e) => {
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const stageX = (pointer.x - stagePos.x) / stageScale;
    const stageY = (pointer.y - stagePos.y) / stageScale;
    
    // Send cursor position to others
    sendCursorMove(stageX, stageY);
    
    // Selection rectangle
    if (selectionRect) {
      setSelectionRect(prev => ({
        ...prev,
        width: stageX - prev.x,
        height: stageY - prev.y
      }));
      return;
    }
    
    // Drawing
    if (isDrawing && currentDrawing) {
      setCurrentDrawing(prev => ({
        ...prev,
        points: [...prev.points, stageX, stageY]
      }));
    }
  }, [stagePos, stageScale, selectionRect, isDrawing, currentDrawing, sendCursorMove]);

  const handleMouseUp = useCallback(() => {
    // Finalize selection rectangle
    if (selectionRect) {
      const rect = {
        x: Math.min(selectionRect.x, selectionRect.x + selectionRect.width),
        y: Math.min(selectionRect.y, selectionRect.y + selectionRect.height),
        width: Math.abs(selectionRect.width),
        height: Math.abs(selectionRect.height)
      };
      
      // Find elements within selection
      const selected = elements.filter(el => {
        const elRight = el.x + (el.width || 100);
        const elBottom = el.y + (el.height || 100);
        return el.x < rect.x + rect.width && elRight > rect.x &&
               el.y < rect.y + rect.height && elBottom > rect.y;
      });
      
      setSelectedIds(selected.map(el => el.id));
      setSelectionRect(null);
    }
    
    // Finalize drawing or connector
    if (isDrawing && currentDrawing && currentDrawing.points.length > 2) {
      if (currentDrawing.type === 'connector_drawn') {
        // Normalize points to be relative to the drawing's origin
        const pts = currentDrawing.points;
        let minX = pts[0], minY = pts[1];
        for (let i = 0; i < pts.length; i += 2) {
          minX = Math.min(minX, pts[i]);
          minY = Math.min(minY, pts[i + 1]);
        }
        const normalizedPoints = pts.map((p, i) => i % 2 === 0 ? p - minX : p - minY);
        
        // Create a drawn connector with arrows on both ends
        createElement('connector_drawn', minX, minY, {
          properties: {
            points: normalizedPoints,
            stroke: currentDrawing.stroke,
            strokeWidth: currentDrawing.strokeWidth
          }
        });
      } else {
        // Normalize points to be relative to the drawing's origin
        const pts = currentDrawing.points;
        let minX = pts[0], minY = pts[1];
        for (let i = 0; i < pts.length; i += 2) {
          minX = Math.min(minX, pts[i]);
          minY = Math.min(minY, pts[i + 1]);
        }
        const normalizedPoints = pts.map((p, i) => i % 2 === 0 ? p - minX : p - minY);
        
        // Regular drawing - store with proper position and normalized points
        createElement('drawing', minX, minY, {
          properties: {
            points: normalizedPoints,
            stroke: currentDrawing.stroke,
            strokeWidth: currentDrawing.strokeWidth
          }
        });
      }
    }
    
    setIsDrawing(false);
    setCurrentDrawing(null);
  }, [selectionRect, elements, isDrawing, currentDrawing, createElement]);

  // Handle element selection
  const handleElementSelect = useCallback((elementId, e) => {
    if (activeTool !== TOOLS.SELECT) return;
    
    if (e?.evt?.shiftKey) {
      // Multi-select with shift
      setSelectedIds(prev => 
        prev.includes(elementId) 
          ? prev.filter(id => id !== elementId)
          : [...prev, elementId]
      );
    } else {
      setSelectedIds([elementId]);
    }
  }, [activeTool]);

  // Handle drag end
  const handleDragEnd = useCallback(async (elementId, newPos) => {
    if (!canEdit) return;
    
    pushHistory();
    await updateElement(elementId, { x: newPos.x, y: newPos.y });
  }, [canEdit, pushHistory, updateElement]);

  // Handle transform end
  const handleTransformEnd = useCallback(async (elementId, newAttrs) => {
    if (!canEdit) return;
    
    pushHistory();
    await updateElement(elementId, newAttrs);
  }, [canEdit, pushHistory, updateElement]);

  // Handle image upload
  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Upload to server
      const response = await axios.post(`${API}/api/media/upload`, formData);
      const imageUrl = response.data.url;
      
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        const maxSize = 400;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width *= ratio;
          height *= ratio;
        }
        
        createElement('image', stageSize.width / 2 / stageScale - width / 2, stageSize.height / 2 / stageScale - height / 2, {
          width,
          height,
          properties: {
            src: `${API}${imageUrl}`,
            originalWidth: img.width,
            originalHeight: img.height
          }
        });
      };
      img.src = `${API}${imageUrl}`;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [createElement, stageSize, stageScale]);

  // Context menu actions
  const handleContextAction = useCallback((action) => {
    if (!contextMenu) return;
    
    switch (action) {
      case 'sticky_note':
        createElement('sticky_note', contextMenu.stageX - 100, contextMenu.stageY - 100);
        break;
      case 'text':
        createElement('text', contextMenu.stageX, contextMenu.stageY);
        break;
      case 'shape_rect':
        createElement('shape_rect', contextMenu.stageX - 75, contextMenu.stageY - 50);
        break;
      case 'delete':
        handleDeleteSelected();
        break;
      case 'duplicate':
        // Duplicate selected elements
        selectedIds.forEach(id => {
          const element = elements.find(el => el.id === id);
          if (element) {
            createElement(element.type, element.x + 20, element.y + 20, {
              width: element.width,
              height: element.height,
              properties: { ...element.properties }
            });
          }
        });
        break;
      case 'bring_front':
        if (selectedIds.length > 0) {
          const maxZ = Math.max(...elements.map(el => el.z_index || 0));
          selectedIds.forEach(id => {
            updateElement(id, { z_index: maxZ + 1 });
          });
        }
        break;
      case 'send_back':
        if (selectedIds.length > 0) {
          const minZ = Math.min(...elements.map(el => el.z_index || 0));
          selectedIds.forEach(id => {
            updateElement(id, { z_index: minZ - 1 });
          });
        }
        break;
      default:
        break;
    }
    
    setContextMenu(null);
  }, [contextMenu, createElement, handleDeleteSelected, selectedIds, elements, updateElement]);

  // Render element by type
  const renderElement = useCallback((element) => {
    const isSelected = selectedIds.includes(element.id);
    const isBeingEdited = activeEdits[element.id];
    
    const commonProps = {
      element,
      isSelected,
      isBeingEdited,
      onSelect: handleElementSelect,
      onDragEnd: handleDragEnd,
      onTransformEnd: handleTransformEnd,
      onUpdate: updateElement,
      canEdit
    };
    
    switch (element.type) {
      case 'sticky_note':
        return <StickyNote key={element.id} {...commonProps} />;
      case 'text':
        return <TextElement key={element.id} {...commonProps} />;
      case 'shape_rect':
      case 'shape_circle':
      case 'shape_arrow':
      case 'connector':
      case 'connector_vertical':
      case 'connector_drawn':
        return <ShapeElement key={element.id} {...commonProps} />;
      case 'drawing':
        return <DrawingElement key={element.id} {...commonProps} />;
      case 'image':
        return <ImageElement key={element.id} {...commonProps} />;
      default:
        return null;
    }
  }, [selectedIds, activeEdits, handleElementSelect, handleDragEnd, handleTransformEnd, updateElement, canEdit]);

  if (loading) {
    return (
      <Layout>
        <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  // Create automation flow component
  const createAutomationFlow = (title, connectionType) => {
    // Create white card element for automation
    const centerX = (stageRef.current?.width() / 2 - stagePos.x) / stageScale - 100;
    const centerY = (stageRef.current?.height() / 2 - stagePos.y) / stageScale - 50;
    
    createElement('sticky_note', centerX, centerY, {
      properties: {
        text: `${title}\n\n${connectionType ? `Connected to: ${connectionType}` : 'Not connected'}`,
        color: '#FFFFFF'
      }
    });
    
    toast.success(`Created ${title} component`);
    setShowAutomationPopover(false);
    setAutomationPrompt('');
  };

  // Flow start type configurations
  const FLOW_START_TYPES = {
    trigger: { label: 'Trigger', icon: Zap, description: 'Start with an event trigger', color: '#22C55E' },
    action: { label: 'Action', icon: Play, description: 'Start with an action step', color: '#3B82F6' },
    decision: { label: 'Decision', icon: Diamond, description: 'Start with a decision point', color: '#F59E0B' },
    process: { label: 'Process', icon: Box, description: 'Start with a process step', color: '#8B5CF6' },
    endpoint: { label: 'End Point', icon: Flag, description: 'Start with an endpoint', color: '#EF4444' }
  };

  const TILE_COLORS = [
    { color: '#22C55E', name: 'Green' },
    { color: '#3B82F6', name: 'Blue' },
    { color: '#F59E0B', name: 'Amber' },
    { color: '#8B5CF6', name: 'Purple' },
    { color: '#EF4444', name: 'Red' },
    { color: '#EC4899', name: 'Pink' },
    { color: '#06B6D4', name: 'Cyan' },
    { color: '#FFFFFF', name: 'White' }
  ];

  const ARROW_COLORS = [
    { color: '#000000', name: 'Black' },
    { color: '#6B7280', name: 'Gray' },
    { color: '#3B82F6', name: 'Blue' },
    { color: '#22C55E', name: 'Green' },
    { color: '#EF4444', name: 'Red' },
    { color: '#F59E0B', name: 'Amber' }
  ];

  // Hierarchical tree helpers for asymmetric flowcharts
  const addChildToTreeNode = (nodeId) => {
    setTreeRoot(prev => {
      const addChild = (node) => {
        if (node.id === nodeId) {
          if (node.children.length >= 5) return node;
          nodeCounterRef.current += 1;
          return {
            ...node,
            children: [...node.children, { 
              id: `node-${nodeCounterRef.current}`, 
              label: `Node ${nodeCounterRef.current}`,
              children: []
            }]
          };
        }
        return {
          ...node,
          children: node.children.map(addChild)
        };
      };
      return addChild(prev);
    });
  };

  const removeChildFromTreeNode = (nodeId) => {
    setTreeRoot(prev => {
      const removeLastChild = (node) => {
        if (node.id === nodeId) {
          if (node.children.length === 0) return node;
          return {
            ...node,
            children: node.children.slice(0, -1)
          };
        }
        return {
          ...node,
          children: node.children.map(removeLastChild)
        };
      };
      return removeLastChild(prev);
    });
  };

  const resetTreeRoot = () => {
    nodeCounterRef.current = 1;
    setTreeRoot({ id: 'root', label: 'Start', children: [] });
  };

  // Preset templates for common flowchart patterns
  const FLOW_TEMPLATES = {
    yesNo: {
      name: 'Yes/No Decision',
      icon: '⚖️',
      description: 'Binary decision point',
      tree: {
        id: 'root', label: 'Decision', children: [
          { id: 'yes', label: 'Yes', children: [] },
          { id: 'no', label: 'No', children: [] }
        ]
      }
    },
    threeway: {
      name: '3-Way Split',
      icon: '🔀',
      description: 'Three parallel paths',
      tree: {
        id: 'root', label: 'Start', children: [
          { id: 'path1', label: 'Path A', children: [] },
          { id: 'path2', label: 'Path B', children: [] },
          { id: 'path3', label: 'Path C', children: [] }
        ]
      }
    },
    sequential: {
      name: 'Sequential',
      icon: '➡️',
      description: 'Step-by-step process',
      tree: {
        id: 'root', label: 'Step 1', children: [
          { id: 'step2', label: 'Step 2', children: [
            { id: 'step3', label: 'Step 3', children: [] }
          ]}
        ]
      }
    },
    ifElse: {
      name: 'If-Else Chain',
      icon: '🔄',
      description: 'Conditional branching',
      tree: {
        id: 'root', label: 'Condition', children: [
          { id: 'if', label: 'If True', children: [
            { id: 'action1', label: 'Action', children: [] }
          ]},
          { id: 'else', label: 'Else', children: [
            { id: 'action2', label: 'Fallback', children: [] }
          ]}
        ]
      }
    },
    funnel: {
      name: 'Funnel',
      icon: '📊',
      description: 'Converging paths',
      tree: {
        id: 'root', label: 'Start', children: [
          { id: 'a', label: 'Input A', children: [
            { id: 'merge', label: 'Process', children: [] }
          ]},
          { id: 'b', label: 'Input B', children: [] },
          { id: 'c', label: 'Input C', children: [] }
        ]
      }
    }
  };

  const applyTemplate = (templateKey) => {
    const template = FLOW_TEMPLATES[templateKey];
    if (template) {
      nodeCounterRef.current = 10; // Reset counter to avoid conflicts
      setTreeRoot(JSON.parse(JSON.stringify(template.tree))); // Deep clone
    }
  };

  // Count total nodes in tree
  const countTreeNodes = (node) => {
    let count = 1;
    node.children.forEach(child => {
      count += countTreeNodes(child);
    });
    return count;
  };

  // Calculate tree width at each level for layout
  const calculateSubtreeWidth = (node, tileWidth, gap) => {
    if (node.children.length === 0) {
      return tileWidth;
    }
    let totalWidth = 0;
    node.children.forEach((child, idx) => {
      totalWidth += calculateSubtreeWidth(child, tileWidth, gap);
      if (idx < node.children.length - 1) totalWidth += gap;
    });
    return Math.max(tileWidth, totalWidth);
  };

  // Create flowchart from hierarchical tree structure
  const createFlowchartFromTree = () => {
    const startType = FLOW_START_TYPES[flowStartType];
    const centerX = (stageRef.current?.width() / 2 - stagePos.x) / stageScale;
    const startY = (stageRef.current?.height() / 4 - stagePos.y) / stageScale;
    
    const TILE_WIDTH = 130;
    const TILE_HEIGHT = 60;
    const HORIZONTAL_GAP = 20;
    const VERTICAL_GAP = 80;
    const ARROW_LENGTH = 40;

    const getTextColor = (bgColor) => {
      return bgColor === '#FFFFFF' || bgColor === '#F59E0B' || bgColor === '#F3F4F6' ? '#000000' : '#FFFFFF';
    };

    // Recursive function to render tree
    const renderNode = (node, x, y, isRoot = false, depth = 0) => {
      const hasChildren = node.children.length > 0;
      const isLeaf = !hasChildren;
      
      // Determine tile styling
      const tileFill = isLeaf && !isRoot ? '#F3F4F6' : flowTileColor;
      const tileStroke = isLeaf && !isRoot ? '#D1D5DB' : (flowTileColor === '#FFFFFF' ? '#D1D5DB' : flowTileColor);
      const tileText = isRoot ? startType.label : node.label;
      
      // Create tile
      createElement('shape_rect', x - TILE_WIDTH / 2, y, {
        width: TILE_WIDTH,
        height: TILE_HEIGHT,
        properties: {
          fill: tileFill,
          stroke: tileStroke,
          strokeWidth: 2,
          text: tileText,
          fontSize: isRoot ? 14 : 12,
          textColor: getTextColor(tileFill),
          cornerRadius: 6
        }
      });

      if (!hasChildren) return;

      // Calculate positions for children
      const totalWidth = calculateSubtreeWidth(node, TILE_WIDTH, HORIZONTAL_GAP);
      let currentX = x - totalWidth / 2;
      
      node.children.forEach((child, idx) => {
        const childWidth = calculateSubtreeWidth(child, TILE_WIDTH, HORIZONTAL_GAP);
        const childX = currentX + childWidth / 2;
        const childY = y + TILE_HEIGHT + ARROW_LENGTH + VERTICAL_GAP;
        
        // Draw arrow to child
        const startPointX = x;
        const startPointY = y + TILE_HEIGHT;
        const endPointX = childX;
        const endPointY = childY;
        
        createElement('shape_arrow', startPointX, startPointY, {
          properties: {
            points: [0, 0, endPointX - startPointX, endPointY - startPointY - TILE_HEIGHT],
            stroke: flowArrowColor,
            strokeWidth: 2,
            pointerLength: 8,
            pointerWidth: 8
          }
        });
        
        // Render child subtree
        renderNode(child, childX, childY, false, depth + 1);
        
        currentX += childWidth + HORIZONTAL_GAP;
      });
    };

    // Start rendering from root
    renderNode(treeRoot, centerX, startY, true);
    
    const totalNodes = countTreeNodes(treeRoot);
    toast.success(`Created flowchart with ${totalNodes} node${totalNodes > 1 ? 's' : ''}`);
    setShowAutomationPopover(false);
    resetTreeRoot();
  };

  // Legacy simple flowchart (keeping for single-tile quick add)
  const createFlowchartStart = () => {
    const startType = FLOW_START_TYPES[flowStartType];
    const centerX = (stageRef.current?.width() / 2 - stagePos.x) / stageScale;
    const centerY = (stageRef.current?.height() / 2 - stagePos.y) / stageScale;
    
    // Create the starting tile
    createElement('shape_rect', centerX - 75, centerY - 50, {
      width: 150,
      height: 80,
      properties: {
        fill: flowTileColor,
        stroke: flowTileColor === '#FFFFFF' ? '#D1D5DB' : flowTileColor,
        strokeWidth: 2,
        text: startType.label,
        fontSize: 18,
        textColor: flowTileColor === '#FFFFFF' || flowTileColor === '#F59E0B' ? '#000000' : '#FFFFFF',
        cornerRadius: 8
      }
    });
    
    // Create a downward arrow (single direction) from the tile
    createElement('shape_arrow', centerX, centerY + 40, {
      properties: {
        points: [0, 0, 0, 60],
        stroke: flowArrowColor,
        strokeWidth: 2,
        pointerLength: 12,
        pointerWidth: 12
      }
    });
    
    // Create a placeholder next step tile
    createElement('shape_rect', centerX - 75, centerY + 110, {
      width: 150,
      height: 80,
      properties: {
        fill: '#F3F4F6',
        stroke: '#D1D5DB',
        strokeWidth: 2,
        text: 'Next Step',
        fontSize: 14,
        textColor: '#6B7280',
        cornerRadius: 8
      }
    });
    
    toast.success(`Created ${startType.label} flowchart`);
    setShowAutomationPopover(false);
  };

  // Create a single tile component
  const createSingleTile = (title) => {
    const centerX = (stageRef.current?.width() / 2 - stagePos.x) / stageScale;
    const centerY = (stageRef.current?.height() / 2 - stagePos.y) / stageScale;
    
    createElement('shape_rect', centerX - 75, centerY - 40, {
      width: 150,
      height: 80,
      properties: {
        fill: flowTileColor,
        stroke: flowTileColor === '#FFFFFF' ? '#D1D5DB' : flowTileColor,
        strokeWidth: 2,
        text: title || 'New Tile',
        fontSize: 16,
        textColor: flowTileColor === '#FFFFFF' || flowTileColor === '#F59E0B' ? '#000000' : '#FFFFFF',
        cornerRadius: 8
      }
    });
    
    toast.success(`Created tile: ${title || 'New Tile'}`);
    setShowAutomationPopover(false);
    setAutomationPrompt('');
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-100 dark:bg-slate-900 overflow-hidden" data-testid="canvas-editor">
        {/* Canvas Header */}
        <div className="h-12 border-b bg-background flex items-center justify-between px-4 shrink-0 z-[100] relative">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/canvas')}
              data-testid="back-to-canvas-list"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-foreground">{canvas?.name || 'Untitled Canvas'}</h1>
              {!canEdit && (
                <span className="text-xs text-muted-foreground">View only</span>
              )}
            </div>
          </div>
          
          {/* Quick Create Automation Buttons */}
          {canEdit && (
            <div className="flex items-center gap-2">
              <Popover open={showAutomationPopover} onOpenChange={setShowAutomationPopover}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" data-testid="quick-create-btn">
                    <Plus className="h-4 w-4" />
                    Quick Create
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[420px]" side="bottom" align="end">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <GitBranch className="h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-semibold text-sm">Flowchart Builder</h4>
                        <p className="text-xs text-muted-foreground">Design your flow structure</p>
                      </div>
                    </div>

                    {/* Start Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Start With</Label>
                      <Select value={flowStartType} onValueChange={setFlowStartType}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(FLOW_START_TYPES).map(([key, type]) => {
                            const Icon = type.icon;
                            return (
                              <SelectItem key={key} value={key}>
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" style={{ color: type.color }} />
                                  <span>{type.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Template Presets */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Quick Templates</Label>
                      <div className="grid grid-cols-5 gap-1">
                        {Object.entries(FLOW_TEMPLATES).map(([key, template]) => (
                          <button
                            key={key}
                            onClick={() => applyTemplate(key)}
                            className="flex flex-col items-center p-2 rounded-md border border-transparent hover:border-primary hover:bg-muted/50 transition-all group"
                            title={template.description}
                          >
                            <span className="text-lg">{template.icon}</span>
                            <span className="text-[9px] text-muted-foreground group-hover:text-foreground text-center leading-tight mt-1">
                              {template.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tree Structure Builder */}
                    <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Flow Structure</Label>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-xs"
                          onClick={resetTreeRoot}
                        >
                          Reset
                        </Button>
                      </div>
                      
                      {/* Interactive Tree Preview */}
                      <div className="min-h-[100px] max-h-[200px] overflow-auto">
                        {(() => {
                          // Recursive component to render tree nodes
                          const TreeNode = ({ node, depth = 0, isRoot = false }) => (
                            <div className="flex flex-col items-center">
                              {/* Node */}
                              <div className="flex items-center gap-1 mb-1">
                                <div 
                                  className="px-2 py-1 rounded text-[10px] font-medium flex items-center gap-1 border"
                                  style={{ 
                                    backgroundColor: node.children.length === 0 && !isRoot ? '#F3F4F6' : (flowTileColor === '#FFFFFF' ? '#F9FAFB' : flowTileColor),
                                    borderColor: node.children.length === 0 && !isRoot ? '#D1D5DB' : (flowTileColor === '#FFFFFF' ? '#D1D5DB' : flowTileColor),
                                    color: (flowTileColor === '#FFFFFF' || flowTileColor === '#F59E0B' || (node.children.length === 0 && !isRoot)) ? '#374151' : '#FFFFFF'
                                  }}
                                >
                                  {isRoot ? FLOW_START_TYPES[flowStartType].label : node.label}
                                </div>
                                <div className="flex items-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => removeChildFromTreeNode(node.id)}
                                    disabled={node.children.length === 0}
                                    title="Remove child"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => addChildToTreeNode(node.id)}
                                    disabled={node.children.length >= 5 || depth >= 4}
                                    title="Add child"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Children */}
                              {node.children.length > 0 && (
                                <>
                                  {/* Connector line down */}
                                  <div 
                                    className="w-0.5 h-3" 
                                    style={{ backgroundColor: flowArrowColor }}
                                  />
                                  {/* Horizontal connector for multiple children */}
                                  {node.children.length > 1 && (
                                    <div 
                                      className="h-0.5" 
                                      style={{ 
                                        backgroundColor: flowArrowColor,
                                        width: `${Math.max(60, node.children.length * 80 - 20)}px`
                                      }}
                                    />
                                  )}
                                  {/* Children row */}
                                  <div className="flex gap-2 mt-1">
                                    {node.children.map((child, idx) => (
                                      <div key={child.id} className="flex flex-col items-center">
                                        {/* Connector line down to child */}
                                        <div 
                                          className="w-0.5 h-2" 
                                          style={{ backgroundColor: flowArrowColor }}
                                        />
                                        <TreeNode node={child} depth={depth + 1} />
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                          
                          return <TreeNode node={treeRoot} isRoot={true} />;
                        })()}
                      </div>
                      
                      {/* Help text */}
                      <p className="text-[10px] text-muted-foreground text-center pt-1 border-t">
                        Click +/- on each node to add/remove branches
                      </p>
                    </div>

                    {/* Color Options */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Tile Color */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Tile Color</Label>
                        <div className="flex flex-wrap gap-1">
                          {TILE_COLORS.map(({ color, name }) => (
                            <button
                              key={color}
                              title={name}
                              className={`w-6 h-6 rounded border-2 transition-all ${
                                flowTileColor === color 
                                  ? 'border-primary scale-110' 
                                  : 'border-transparent hover:border-gray-400'
                              }`}
                              style={{ 
                                backgroundColor: color,
                                border: color === '#FFFFFF' ? '1px solid #D1D5DB' : undefined
                              }}
                              onClick={() => setFlowTileColor(color)}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Arrow Color */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Arrow Color</Label>
                        <div className="flex flex-wrap gap-1">
                          {ARROW_COLORS.map(({ color, name }) => (
                            <button
                              key={color}
                              title={name}
                              className={`w-6 h-6 rounded-full border-2 transition-all ${
                                flowArrowColor === color 
                                  ? 'border-primary scale-110' 
                                  : 'border-transparent hover:border-gray-400'
                              }`}
                              style={{ backgroundColor: color }}
                              onClick={() => setFlowArrowColor(color)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Create Flow Button */}
                    <Button 
                      className="w-full gap-2" 
                      onClick={createFlowchartFromTree}
                      data-testid="create-flowchart-btn"
                    >
                      <Workflow className="h-4 w-4" />
                      Create Flowchart
                    </Button>
                    
                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-background px-2 text-muted-foreground">or add a single tile</span>
                      </div>
                    </div>

                    {/* Custom Tile Creation */}
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Tile title (e.g., Send Email)"
                          value={automationPrompt}
                          onChange={(e) => setAutomationPrompt(e.target.value)}
                          className="text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && automationPrompt.trim()) {
                              createSingleTile(automationPrompt);
                            }
                          }}
                        />
                        <Button 
                          size="sm"
                          variant="secondary"
                          onClick={() => createSingleTile(automationPrompt)}
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                    {/* Quick Add Presets */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">Quick Add</Label>
                      <div className="flex flex-wrap gap-1">
                        {['Trigger', 'Action', 'Condition', 'Delay', 'End'].map((preset) => (
                          <Button
                            key={preset}
                            variant="outline"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => createSingleTile(preset)}
                          >
                            {preset}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
          
          <CanvasPresence users={activeUsers} isConnected={isConnected} />
        </div>
        
        {/* Canvas Container */}
      <div 
        ref={containerRef} 
        className="flex-1 relative overflow-hidden"
        style={{ backgroundColor: boardBackgroundColor }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Toolbar - inside canvas container so it's positioned relative to canvas */}
        {canEdit && (
          <CanvasToolbar
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            toolOptions={toolOptions}
            setToolOptions={setToolOptions}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            onImageUpload={() => fileInputRef.current?.click()}
            stageScale={stageScale}
            setStageScale={setStageScale}
          />
        )}
        
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          x={stagePos.x}
          y={stagePos.y}
          scaleX={stageScale}
          scaleY={stageScale}
          draggable={activeTool === TOOLS.PAN}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onDragEnd={(e) => {
            // Only update stage position if dragging the stage itself (not an element)
            if (activeTool === TOOLS.PAN && e.target === e.target.getStage()) {
              setStagePos({ x: e.target.x(), y: e.target.y() });
            }
          }}
        >
          {/* Background Layer - infinite canvas background */}
          <Layer listening={false}>
            <Rect
              x={-10000}
              y={-10000}
              width={20000}
              height={20000}
              fill={boardBackgroundColor}
            />
          </Layer>
          
          {/* Elements Layer */}
          <Layer>
            {/* Sort by z_index */}
            {elements
              .slice()
              .sort((a, b) => (a.z_index || 0) - (b.z_index || 0))
              .map(renderElement)}
            
            {/* Current drawing in progress */}
            {currentDrawing && (
              <Line
                points={currentDrawing.points}
                stroke={currentDrawing.stroke}
                strokeWidth={currentDrawing.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  activeTool === TOOLS.ERASER ? 'destination-out' : 'source-over'
                }
              />
            )}
            
            {/* Selection rectangle */}
            {selectionRect && (
              <Rect
                x={selectionRect.x}
                y={selectionRect.y}
                width={selectionRect.width}
                height={selectionRect.height}
                fill="rgba(59, 130, 246, 0.1)"
                stroke="#3B82F6"
                strokeWidth={1 / stageScale}
              />
            )}
            
            {/* Transformer for selected elements */}
            <Transformer
              ref={transformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                // Limit resize
                if (newBox.width < 20 || newBox.height < 20) {
                  return oldBox;
                }
                return newBox;
              }}
            />
          </Layer>
          
          {/* Cursors Layer */}
          <Layer listening={false}>
            <CanvasCursors cursors={cursors} currentUserId={currentUser?.user_id || currentUser?.id} />
          </Layer>
        </Stage>
        
        {/* Context Menu */}
        {contextMenu && (
          <CanvasContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            hasSelection={selectedIds.length > 0}
            canEdit={canEdit}
            onAction={handleContextAction}
            onClose={() => setContextMenu(null)}
          />
        )}
        
        {/* Text Properties Panel - shown when a single text element is selected */}
        {canEdit && selectedIds.length === 1 && (() => {
          const selectedElement = elements.find(el => el.id === selectedIds[0]);
          if (selectedElement && selectedElement.type === 'text') {
            // Calculate screen position of the element
            const screenX = selectedElement.x * stageScale + stagePos.x;
            const screenY = selectedElement.y * stageScale + stagePos.y;
            return (
              <TextPropertiesPanel
                element={selectedElement}
                onUpdate={updateElement}
                position={{ x: screenX, y: screenY }}
              />
            );
          }
          return null;
        })()}
        
        {/* Sticky Note Properties Panel - shown when a single sticky note is selected */}
        {canEdit && selectedIds.length === 1 && (() => {
          const selectedElement = elements.find(el => el.id === selectedIds[0]);
          if (selectedElement && selectedElement.type === 'sticky_note') {
            // Calculate screen position of the element
            const screenX = selectedElement.x * stageScale + stagePos.x;
            const screenY = selectedElement.y * stageScale + stagePos.y;
            return (
              <StickyNotePropertiesPanel
                element={selectedElement}
                onUpdate={updateElement}
                position={{ x: screenX, y: screenY }}
              />
            );
          }
          return null;
        })()}
        
        {/* Arrow/Connector Properties Panel - shown when a single arrow or connector is selected */}
        {canEdit && selectedIds.length === 1 && (() => {
          const selectedElement = elements.find(el => el.id === selectedIds[0]);
          const arrowTypes = ['shape_arrow', 'connector', 'connector_vertical', 'connector_drawn'];
          if (selectedElement && arrowTypes.includes(selectedElement.type)) {
            const screenX = selectedElement.x * stageScale + stagePos.x;
            const screenY = selectedElement.y * stageScale + stagePos.y;
            return (
              <ArrowPropertiesPanel
                element={selectedElement}
                onUpdate={updateElement}
                position={{ x: screenX, y: screenY }}
              />
            );
          }
          return null;
        })()}
        
        {/* Shape Properties Panel - shown when a single shape (rect/circle) is selected */}
        {canEdit && selectedIds.length === 1 && (() => {
          const selectedElement = elements.find(el => el.id === selectedIds[0]);
          const shapeTypes = ['shape_rect', 'shape_circle'];
          if (selectedElement && shapeTypes.includes(selectedElement.type)) {
            const screenX = selectedElement.x * stageScale + stagePos.x;
            const screenY = selectedElement.y * stageScale + stagePos.y;
            return (
              <ShapePropertiesPanel
                element={selectedElement}
                onUpdate={updateElement}
                position={{ x: screenX, y: screenY }}
              />
            );
          }
          return null;
        })()}
        
        {/* Drawing Properties Panel - shown when a single drawing is selected */}
        {canEdit && selectedIds.length === 1 && (() => {
          const selectedElement = elements.find(el => el.id === selectedIds[0]);
          if (selectedElement && selectedElement.type === 'drawing') {
            const screenX = selectedElement.x * stageScale + stagePos.x;
            const screenY = selectedElement.y * stageScale + stagePos.y;
            return (
              <DrawingPropertiesPanel
                element={selectedElement}
                onUpdate={updateElement}
                position={{ x: screenX, y: screenY }}
              />
            );
          }
          return null;
        })()}
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
      </div>
    </Layout>
  );
}
